import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { downloadCSV, downloadXLSX } from '@/lib/exports';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

const AdminScoringTab = () => {
  const [entries, setEntries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);   // reset-confirm modal
  const [resetScope, setResetScope] = useState('all'); // 'all' | 'entry'
  const [resetBusy, setResetBusy] = useState(false);

  const reload = async () => {
    try {
      const { data } = await ax.get(`${API}/admin/nominations-filtered`, { params: {} });
      const withScores = data.filter((e) => (e.jury_score_count || 0) > 0);
      setEntries(withScores);
      setSelected(withScores.length ? withScores[0].id : null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load.'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    setLoading(true);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    setDetailLoading(true);
    (async () => {
      try {
        const { data } = await ax.get(`${API}/admin/nominations/${selected}/scoring`);
        setDetail(data);
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load scoring.'); }
      finally { setDetailLoading(false); }
    })();
  }, [selected]);

  const framework = detail?.framework;
  const criteria = framework?.criteria || [];

  // Detect legacy scores (no criterion_scores/weighted_total) so we still show them.
  const scores = detail?.scores || [];

  const exportRows = scores.map((s) => {
    const isV2 = s.weighted_total != null || (s.criterion_scores && Object.keys(s.criterion_scores).length);
    const base = {
      Juror: s.juror_name,
      Framework: s.framework_name || (isV2 ? framework?.name : 'Legacy'),
      'Weighted Total (/100)': s.weighted_total != null ? s.weighted_total : s.total,
      Percentage: s.percentage != null ? s.percentage : '',
      Strengths: s.strengths || '',
      'Areas for Improvement': s.improvements || '',
      Comments: s.comments,
    };
    if (isV2) {
      (s.weighted_breakdown || []).forEach((b) => {
        base[`${b.label} (${b.weight}%) — score`] = b.score;
        base[`${b.label} — weighted`] = b.weighted;
      });
    } else {
      Object.assign(base, {
        Strategy: s.strategy, Creativity: s.creativity, Execution: s.execution, Impact: s.impact, Innovation: s.innovation,
      });
    }
    return base;
  });

  const avgPerCriterion = (key) => {
    if (!scores.length) return 0;
    const vals = scores.map((s) => (s.criterion_scores && s.criterion_scores[key]) || 0);
    return vals.reduce((a, b) => a + b, 0) / scores.length;
  };

  // Reset — wipes scores for the chosen scope, then reloads the list.
  // This is a nuclear action: no soft-delete, no undo. Used to clear internal
  // test scores before the real jury evaluation window opens.
  const doReset = async () => {
    setResetBusy(true);
    try {
      const body = { confirm: true };
      if (resetScope === 'entry' && selected) body.nomination_id = selected;
      const { data } = await ax.post(`${API}/admin/scores/reset`, body);
      toast.success(`Reset complete — ${data.deleted} evaluation${data.deleted === 1 ? '' : 's'} cleared.`);
      setResetOpen(false);
      setDetail(null);
      setLoading(true);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed.');
    } finally { setResetBusy(false); }
  };

  return (
    <div data-testid="admin-scoring-tab">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <p className="text-[var(--ink-muted)] text-sm">Per-entry jury scoring — criterion-wise breakdown per juror, weighted total.</p>
        <div className="flex gap-2 flex-wrap">
          <a
            href={`${API}/admin/evaluations/export?fmt=csv`}
            className="border border-[var(--gold-faint)] hover:border-[var(--gold)] text-[var(--ink)] hover:text-[var(--gold)] text-[10px] tracking-[0.3em] uppercase px-3 py-2 transition-colors"
            data-testid="export-all-evaluations-csv"
          >Export All (CSV)</a>
          <a
            href={`${API}/admin/evaluations/export?fmt=xlsx`}
            className="border border-[var(--gold-faint)] hover:border-[var(--gold)] text-[var(--ink)] hover:text-[var(--gold)] text-[10px] tracking-[0.3em] uppercase px-3 py-2 transition-colors"
            data-testid="export-all-evaluations-xlsx"
          >Export All (Excel)</a>
          <button
            type="button"
            onClick={() => { setResetScope('all'); setResetOpen(true); }}
            data-testid="scoring-reset-btn"
            className="border border-[var(--terracotta-soft)] hover:border-[var(--terracotta)] text-[var(--terracotta)] hover:bg-[var(--terracotta)]/10 text-[10px] tracking-[0.3em] uppercase px-3 py-2 transition-colors"
          >Reset Scores</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6">
        {/* Left: entry list */}
        <div className="border border-[var(--gold-faint)] max-h-[70vh] overflow-y-auto">
          {loading ? <div className="p-6 text-[var(--ink-muted)] text-xs tracking-[0.3em] uppercase">Loading…</div> :
           entries.length === 0 ? <div className="p-6 text-[var(--ink-muted)] text-sm" data-testid="scoring-empty">No entries have been scored yet.</div> :
           entries.map((e) => (
             <button key={e.id} onClick={() => setSelected(e.id)} data-testid={`scoring-entry-${e.id}`} className={`block w-full text-left p-4 border-b border-[var(--gold-faint)]/40 transition-colors ${selected === e.id ? 'bg-[var(--gold)]/10' : 'hover:bg-[var(--gold)]/5'}`}>
               <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--gold)] truncate">{e.category}</div>
               <div className="font-[Libre_Baskerville] text-[var(--ink)] mt-1 leading-tight">{e.entry_title || 'Untitled'}</div>
               <div className="text-[var(--ink-muted)] text-xs mt-1">{e.brand_name} · {e.jury_score_count} score{e.jury_score_count > 1 ? 's' : ''} {e.jury_avg != null && <span className="text-[var(--gold)]">· avg {e.jury_avg}</span>}</div>
             </button>
           ))}
        </div>

        {/* Right: scoring detail */}
        <div className="border border-[var(--gold-faint)] p-6 min-h-[300px]" data-testid="scoring-detail">
          {detailLoading ? <div className="text-[var(--ink-muted)] text-xs tracking-[0.3em] uppercase">Loading…</div> :
           !detail ? <div className="text-[var(--ink-muted)] text-sm">Select an entry to view jury scores.</div> :
           <>
             <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
               <div>
                 <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--gold)]">{detail.nomination.category}</div>
                 <h3 className="font-[Libre_Baskerville] text-2xl text-[var(--ink)] mt-1">{detail.nomination.entry_title}</h3>
                 <div className="text-[var(--ink-muted)] text-xs mt-1">{detail.nomination.brand_name}</div>
                 {framework && (
                   <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--gold)]/80 mt-2" data-testid="scoring-framework-name">
                     Framework: {framework.name}
                   </div>
                 )}
               </div>
               <div className="text-right">
                 <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)]">Average</div>
                 <div className="font-[Libre_Baskerville] text-3xl text-[var(--gold)]" data-testid="scoring-avg">
                   {detail.average_total ?? '—'}<span className="text-[var(--ink-muted)] text-base">/100</span>
                 </div>
                 <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)]">{detail.submitted_count} juror{detail.submitted_count !== 1 ? 's' : ''}{detail.conflict_count > 0 && ` · ${detail.conflict_count} conflict`}</div>
               </div>
             </div>

             <div className="flex gap-2 mb-4">
               <button onClick={() => downloadCSV(`adaura-scoring-${detail.nomination.id.slice(0,8)}.csv`, exportRows)} className="border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--bg)] text-[10px] tracking-[0.3em] uppercase px-3 py-1 transition-colors" data-testid="export-entry-csv">CSV</button>
               <button onClick={() => downloadXLSX(`adaura-scoring-${detail.nomination.id.slice(0,8)}.xlsx`, exportRows, 'Scores')} className="border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--bg)] text-[10px] tracking-[0.3em] uppercase px-3 py-1 transition-colors" data-testid="export-entry-xlsx">Excel</button>
             </div>

             {scores.length === 0 ? (
               <div className="text-[var(--ink-muted)] text-sm italic">No submitted evaluations yet.</div>
             ) : (
               <div className="overflow-x-auto">
                 {criteria.length > 0 && scores.some((s) => s.criterion_scores && Object.keys(s.criterion_scores).length) ? (
                   <table className="w-full text-sm" data-testid="scoring-table">
                     <thead className="bg-[var(--bg-deep)]/50">
                       <tr>
                         <th className="text-left px-3 py-2 text-[10px] tracking-[0.25em] uppercase text-[var(--ink-muted)] font-normal">Juror</th>
                         {criteria.map((c) => (
                           <th key={c.key} className="text-left px-3 py-2 text-[10px] tracking-[0.25em] uppercase text-[var(--ink-muted)] font-normal">
                             {c.label}<br/><span className="text-[9px] text-[var(--gold)]/80">{c.weight}%</span>
                           </th>
                         ))}
                         <th className="text-left px-3 py-2 text-[10px] tracking-[0.25em] uppercase text-[var(--ink-muted)] font-normal">Total /100</th>
                       </tr>
                     </thead>
                     <tbody>
                       {scores.map((s) => (
                         <tr key={s.juror_id} className="border-t border-[var(--gold-faint)]/30">
                           <td className="px-3 py-3 text-[var(--ink)]">{s.juror_name}</td>
                           {criteria.map((c) => (
                             <td key={c.key} className="px-3 py-3 text-[var(--ink-muted)] numeral">
                               {(s.criterion_scores && s.criterion_scores[c.key] != null) ? s.criterion_scores[c.key] : '—'}
                             </td>
                           ))}
                           <td className="px-3 py-3 text-[var(--gold)] numeral font-bold">
                             {s.weighted_total != null ? s.weighted_total : s.total}
                           </td>
                         </tr>
                       ))}
                       <tr className="border-t-2 border-[var(--gold)]/40 bg-[var(--gold)]/5">
                         <td className="px-3 py-3 text-[var(--gold)] tracking-[0.2em] uppercase text-[10px]">Average</td>
                         {criteria.map((c) => (
                           <td key={c.key} className="px-3 py-3 text-[var(--gold)] numeral">{avgPerCriterion(c.key).toFixed(1)}</td>
                         ))}
                         <td className="px-3 py-3 text-[var(--gold)] numeral font-bold">{detail.average_total}</td>
                       </tr>
                     </tbody>
                   </table>
                 ) : (
                   // Legacy 5-criterion fallback table (for pre-v2 scores)
                   <table className="w-full text-sm" data-testid="scoring-table-legacy">
                     <thead className="bg-[var(--bg-deep)]/50">
                       <tr>{['Juror', 'Strategy', 'Creativity', 'Execution', 'Impact', 'Innovation', 'Total'].map((h) => (
                         <th key={h} className="text-left px-3 py-2 text-[10px] tracking-[0.25em] uppercase text-[var(--ink-muted)] font-normal">{h}</th>
                       ))}</tr>
                     </thead>
                     <tbody>
                       {scores.map((s) => (
                         <tr key={s.juror_id} className="border-t border-[var(--gold-faint)]/30">
                           <td className="px-3 py-3 text-[var(--ink)]">{s.juror_name}</td>
                           <td className="px-3 py-3 text-[var(--ink-muted)] numeral">{s.strategy}</td>
                           <td className="px-3 py-3 text-[var(--ink-muted)] numeral">{s.creativity}</td>
                           <td className="px-3 py-3 text-[var(--ink-muted)] numeral">{s.execution}</td>
                           <td className="px-3 py-3 text-[var(--ink-muted)] numeral">{s.impact}</td>
                           <td className="px-3 py-3 text-[var(--ink-muted)] numeral">{s.innovation}</td>
                           <td className="px-3 py-3 text-[var(--gold)] numeral font-bold">{s.total}/50</td>
                         </tr>
                       ))}
                     </tbody>
                   </table>
                 )}

                 {/* Juror notes: comments + strengths + improvements */}
                 <div className="mt-6 space-y-5">
                   <h4 className="text-[10px] tracking-[0.3em] uppercase text-[var(--gold)]">Juror Notes</h4>
                   {scores.map((s) => (
                     <div key={s.juror_id + 'c'} className="border-l-2 border-[var(--gold)]/40 pl-4 space-y-2">
                       <div className="text-[var(--ink)] text-sm font-semibold">{s.juror_name}</div>
                       {s.comments && (
                         <div>
                           <div className="text-[9px] tracking-[0.2em] uppercase text-[var(--ink-muted)] mb-0.5">Overall</div>
                           <div className="text-[var(--ink)] text-sm whitespace-pre-wrap">{s.comments}</div>
                         </div>
                       )}
                       {s.strengths && (
                         <div>
                           <div className="text-[9px] tracking-[0.2em] uppercase text-[var(--ink-muted)] mb-0.5">Strengths</div>
                           <div className="text-[var(--ink)]/85 text-sm whitespace-pre-wrap">{s.strengths}</div>
                         </div>
                       )}
                       {s.improvements && (
                         <div>
                           <div className="text-[9px] tracking-[0.2em] uppercase text-[var(--ink-muted)] mb-0.5">Areas for Improvement</div>
                           <div className="text-[var(--ink)]/85 text-sm whitespace-pre-wrap">{s.improvements}</div>
                         </div>
                       )}
                     </div>
                   ))}
                 </div>
               </div>
             )}
           </>}
        </div>
      </div>

      {/* Reset confirmation — nuclear action for wiping test scores before
          the real jury round starts. `entry` scope only clears the currently
          selected entry; `all` clears every score across the platform. */}
      {resetOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="scoring-reset-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !resetBusy && setResetOpen(false)} />
          <div className="relative bg-[var(--bg-alt)] border border-[var(--terracotta-soft)] max-w-md w-full p-7">
            <h3 className="font-[Libre_Baskerville] text-xl text-[var(--ink)] mb-2">Reset jury scores?</h3>
            <p className="text-[var(--ink-muted)] text-sm">
              This permanently deletes evaluations from the scoring database.
              Use it to clear internal test scores before the real jury round begins.
              <strong className="text-[var(--terracotta)]"> This cannot be undone.</strong>
            </p>
            <div className="mt-5 space-y-2">
              <label className={`flex items-start gap-3 p-3 border cursor-pointer ${resetScope === 'all' ? 'border-[var(--gold)] bg-[var(--gold)]/5' : 'border-[var(--gold-faint)]'}`}>
                <input type="radio" name="reset-scope" checked={resetScope === 'all'} onChange={() => setResetScope('all')} className="mt-1" data-testid="reset-scope-all" />
                <div>
                  <div className="text-[var(--ink)] text-sm font-medium">Reset all scores</div>
                  <div className="text-[var(--ink-muted)] text-xs mt-0.5">Clear every juror evaluation across every entry.</div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 border cursor-pointer ${resetScope === 'entry' ? 'border-[var(--gold)] bg-[var(--gold)]/5' : 'border-[var(--gold-faint)]'} ${!selected ? 'opacity-40 pointer-events-none' : ''}`}>
                <input type="radio" name="reset-scope" checked={resetScope === 'entry'} onChange={() => setResetScope('entry')} disabled={!selected} className="mt-1" data-testid="reset-scope-entry" />
                <div>
                  <div className="text-[var(--ink)] text-sm font-medium">Reset only this entry</div>
                  <div className="text-[var(--ink-muted)] text-xs mt-0.5">
                    {selected ? `Clears evaluations for the currently-selected entry only.` : 'Select an entry on the left to enable this option.'}
                  </div>
                </div>
              </label>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setResetOpen(false)}
                disabled={resetBusy}
                className="text-[var(--ink-muted)] text-xs tracking-[0.2em] uppercase px-4 py-2 disabled:opacity-50"
              >Cancel</button>
              <button
                type="button"
                onClick={doReset}
                disabled={resetBusy}
                data-testid="scoring-reset-confirm"
                className="bg-[var(--terracotta)] hover:opacity-90 text-white text-xs tracking-[0.2em] uppercase px-6 py-2 disabled:opacity-50"
              >
                {resetBusy ? 'Resetting…' : (resetScope === 'all' ? 'Reset All' : 'Reset This Entry')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminScoringTab;
